import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import { Capacitor } from "@capacitor/core";
import { notifyExploreCustomerBooking } from "@/lib/explore-booking-notify";
import { isExploreAppVariant } from "@/lib/platform";

export default function PayReturn() {
  const [sp] = useSearchParams();
  const reference = (sp.get("reference") || sp.get("trxref") || "").trim();
  const redirect = (sp.get("redirect") || "").trim();
  const mobileRedirect = (sp.get("mobile_redirect") || "").trim();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<{ status: string; paidAt?: string | null; context?: string; contextId?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [membershipDetails, setMembershipDetails] = useState<{ businessName?: string; membershipCode?: string; expiresAt?: string; planName?: string } | null>(null);

  const redirectTo = useMemo(() => {
    if (!redirect) return null;
    // Only allow relative redirects for safety
    if (redirect.startsWith("/")) return redirect;
    return null;
  }, [redirect]);

  useEffect(() => {
    const run = async () => {
      if (!reference) {
        setError("Missing payment reference.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Call the function directly with fetch to avoid auth issues
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        
        const response = await fetch(`${supabaseUrl}/functions/v1/paystack-verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": anonKey,
          },
          body: JSON.stringify({ reference }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const paymentResult = { 
          status: String(data?.status || "unknown"), 
          paidAt: data?.paidAt || null,
          context: data?.context || null,
          contextId: data?.contextId || null,
        };
        setResult(paymentResult);

        if (
          isExploreAppVariant() &&
          paymentResult.status === "success" &&
          (paymentResult.context === "appointment_deposit" ||
            paymentResult.context === "appointment_group_deposit" ||
            paymentResult.context === "appointment_reservation" ||
            paymentResult.context === "appointment_group_reservation") &&
          paymentResult.contextId
        ) {
          try {
            const sb = supabase as any;
            const isGroup =
              paymentResult.context === "appointment_group_deposit" ||
              paymentResult.context === "appointment_group_reservation";
            let query = sb
              .from("appointments")
              .select(
                `
                id,
                start_datetime,
                confirmation_code,
                appointment_group_id,
                business:businesses ( name, slug ),
                service:services ( name )
              `
              )
              .limit(1);

            query = isGroup
              ? query.eq("appointment_group_id", paymentResult.contextId)
              : query.eq("id", paymentResult.contextId);

            const { data: apt } = await query.maybeSingle();

            if (apt) {
              const business = apt.business as { name?: string; slug?: string } | null;
              const service = apt.service as { name?: string } | null;

              let serviceLabel = service?.name || "your appointment";
              if (isGroup && apt.appointment_group_id) {
                const { data: groupRows } = await sb
                  .from("appointments")
                  .select("service:services ( name )")
                  .eq("appointment_group_id", apt.appointment_group_id);
                const names = (groupRows || [])
                  .map((row) => String((row.service as { name?: string } | null)?.name || "").trim())
                  .filter(Boolean);
                if (names.length > 1) {
                  serviceLabel = `${names[0]} + ${names.length - 1} more`;
                } else if (names[0]) {
                  serviceLabel = names[0];
                }
              }

              await notifyExploreCustomerBooking({
                businessName: business?.name || "Salon",
                businessSlug: business?.slug || "",
                serviceLabel,
                startDatetime: apt.start_datetime,
                pendingPayment: false,
                confirmationCode: apt.confirmation_code,
              });
            }
          } catch (depositNotifyErr) {
            console.warn("[PayReturn] Explore booking confirm notification failed:", depositNotifyErr);
          }
        }

        // If payment is successful and it's a membership payment, fetch membership details
        if (paymentResult.status === "success" && paymentResult.context === "membership_payment" && paymentResult.contextId) {
          try {
            const { data: membershipData, error: memError } = await supabase
              .from("memberships")
              .select(`
                membership_code,
                expires_at,
                business:businesses(name),
                plan:membership_plans(name)
              `)
              .eq("id", paymentResult.contextId)
              .maybeSingle();

            if (!memError && membershipData) {
              setMembershipDetails({
                businessName: (membershipData.business as any)?.name,
                membershipCode: membershipData.membership_code,
                expiresAt: membershipData.expires_at,
                planName: (membershipData.plan as any)?.name,
              });
            }
          } catch (memErr) {
            console.error("Failed to fetch membership details:", memErr);
          }
        }
      } catch (e: any) {
        console.error("Payment verification error:", e);
        setError(e?.message || "Failed to verify payment.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [reference]);

  const openInApp = () => {
    if (!mobileRedirect || Capacitor.isNativePlatform()) return;

    try {
      const mobileUrl = new URL(mobileRedirect);
      if (reference) mobileUrl.searchParams.set("reference", reference);
      if (redirectTo) mobileUrl.searchParams.set("redirect", redirectTo);
      window.location.assign(mobileUrl.toString());
    } catch (e) {
      console.warn("Invalid mobile redirect URL:", e);
    }
  };

  useEffect(() => {
    if (loading || !mobileRedirect || Capacitor.isNativePlatform()) return;

    const timer = window.setTimeout(() => {
      openInApp();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [loading, mobileRedirect, reference, redirectTo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const ok = result?.status === "success";
  const isMembershipPayment = result?.context === "membership_payment";
  const isBalancePayment = result?.context === "appointment_balance";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          {ok ? (
            <CheckCircle2 className="h-6 w-6 text-success" />
          ) : (
            <XCircle className="h-6 w-6 text-destructive" />
          )}
          <div>
            <h1 className="text-lg font-semibold">
              {ok && isMembershipPayment ? "Membership Renewed Successfully!" : ok && isBalancePayment ? "Balance paid" : ok ? "Payment successful" : "Payment not completed"}
            </h1>
            <p className="text-xs text-muted-foreground break-all">Reference: {reference}</p>
          </div>
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        {/* Membership renewal success details */}
        {ok && isMembershipPayment && membershipDetails && (
          <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Your membership has been renewed</p>
                {membershipDetails.businessName && (
                  <p className="text-xs text-muted-foreground mt-1">{membershipDetails.businessName}</p>
                )}
              </div>
            </div>
            
            {membershipDetails.planName && (
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="text-sm font-semibold text-foreground mt-0.5">{membershipDetails.planName}</p>
              </div>
            )}

            {membershipDetails.membershipCode && (
              <div>
                <p className="text-xs text-muted-foreground">Membership Code</p>
                <p className="text-sm font-mono font-semibold text-foreground mt-0.5">{membershipDetails.membershipCode}</p>
              </div>
            )}

            {membershipDetails.expiresAt && (
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Next Expiry Date</p>
                  <p className="text-sm font-semibold text-foreground mt-0.5">
                    {format(parseISO(membershipDetails.expiresAt), "MMM d, yyyy")}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          {!Capacitor.isNativePlatform() && mobileRedirect ? (
            <Button onClick={openInApp} variant="outline">
              Open app
            </Button>
          ) : null}
          {/* For membership payments, don't redirect back to renewal page - show "Done" instead */}
          {ok && isMembershipPayment ? (
            <Button asChild variant="outline">
              <Link to="/">Done</Link>
            </Button>
          ) : redirectTo ? (
            <Button asChild>
              <Link to={redirectTo}>Continue</Link>
            </Button>
          ) : (
            <Button asChild variant="outline">
              <Link to="/">Back to home</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


